import { Check, Clock, Loader2, AlertTriangle, XCircle } from "lucide-react";
import type { RunStatus, RunSummary } from "../types";

interface Props {
  items: RunSummary[];
  onOpen: (id: string) => void;
  onDelete: (id: string) => void;
  pendingDeleteId: string | null;
  onConfirmDelete: (id: string) => void;
  onCancelDelete: () => void;
}

const STATUS_LABELS: Record<RunStatus, string> = {
  processing: "Em processamento",
  completed: "Concluída",
  partial: "Parcial",
  failed: "Falha",
};

const STATUS_BADGE_CLASSES: Record<RunStatus, string> = {
  processing: "bg-blue-50/80 text-blue-600 border-blue-300/60",
  completed: "bg-emerald-50/80 text-emerald-600 border-emerald-300/60",
  partial: "bg-amber-50/80 text-amber-600 border-amber-300/60",
  failed: "bg-red-50/80 text-red-600 border-red-300/60",
};

const STATUS_ICONS: Record<RunStatus, React.ElementType> = {
  processing: Loader2,
  completed: Check,
  partial: AlertTriangle,
  failed: XCircle,
};

const dateTimeFormatter = new Intl.DateTimeFormat("pt-BR", {
  dateStyle: "short",
  timeStyle: "short",
});

function StatusBadge({ status }: { status: RunStatus }) {
  const Icon = STATUS_ICONS[status];
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-body text-xs font-medium whitespace-nowrap ${STATUS_BADGE_CLASSES[status]}`}
    >
      <Icon className={`w-3 h-3 ${status === "processing" ? "animate-spin" : ""}`} />
      {STATUS_LABELS[status]}
    </span>
  );
}

export default function RunHistoryList({
  items,
  onOpen,
  onDelete,
  pendingDeleteId,
  onConfirmDelete,
  onCancelDelete,
}: Props) {
  if (items.length === 0) {
    return (
      <div className="glass rounded-2xl p-10 text-center">
        <p className="text-ink-500 font-body text-sm">Nenhuma pesquisa no histórico ainda.</p>
      </div>
    );
  }

  return (
    <ul className="glass rounded-2xl divide-y divide-white/40 overflow-hidden">
      {items.map((item) => (
        <li
          key={item.id}
          className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-5 py-4"
        >
          <div className="min-w-0 flex-1">
            <p className="font-medium text-ink-100 font-body truncate" title={item.company_name}>
              {item.company_name}
            </p>
            <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-ink-500 font-body">
              <StatusBadge status={item.status} />
              <span className="inline-flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {dateTimeFormatter.format(new Date(item.created_at))}
              </span>
              {item.location && <span>{item.location}</span>}
              <span>{item.completed_category_count}/5 categorias</span>
              <span>{item.source_count} fontes</span>
            </div>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            {pendingDeleteId === item.id ? (
              <>
                <span className="text-sm text-ink-500 font-body">
                  Excluir esta pesquisa? Esta ação não pode ser desfeita.
                </span>
                <button
                  type="button"
                  onClick={() => onConfirmDelete(item.id)}
                  className="text-sm font-medium text-accent-600 hover:underline font-body"
                >
                  Confirmar
                </button>
                <button
                  type="button"
                  onClick={onCancelDelete}
                  className="text-sm font-medium text-ink-400 hover:text-ink-200 font-body"
                >
                  Cancelar
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => onOpen(item.id)}
                  className="px-3 py-2 rounded-lg glass-button text-ink-200 text-sm font-medium font-body transition-all"
                >
                  Reabrir
                </button>
                {item.status !== "processing" && (
                  <button
                    type="button"
                    onClick={() => onDelete(item.id)}
                    className="px-3 py-2 rounded-lg glass-button text-ink-400 hover:text-accent-600 text-sm font-medium font-body transition-all"
                  >
                    Excluir
                  </button>
                )}
              </>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}
